/**
 * When a category change may touch the people fields (RESP1).
 *
 * Extracted from TaskFormModal so the rule can be reasoned about — and
 * tested — on its own. It is the single most consequential decision in
 * the slice: get it wrong and the form silently saves somebody else's
 * name, with nothing on screen to show it happened.
 *
 * Three outcomes, and the ordering between them matters:
 *
 * * ``none``  — the user has decided something. Never overwrite, never
 *   clear, never add. This branch is checked FIRST, so no configuration
 *   can reach past a human choice.
 * * ``apply`` — nothing was decided and this category has usable
 *   defaults: propose them.
 * * ``clear`` — nothing was decided and the previous values were a
 *   proposal from a different category: withdraw it, so the form does
 *   not keep offering people from a category that is no longer chosen.
 *
 * "Decided" includes deliberately emptying the field. "I want nobody on
 * this" is an answer, and the next category must not overrule it.
 */

/** Does this area have people worth proposing? */
export function hasUsableDefaults(area) {
  if (!area || !area.is_active || !area.apply_default_on_task_create) {
    return false;
  }
  const assignee = area.default_assigned_to_user;
  const activeAssignee = assignee && assignee.is_active !== false;
  const activeParticipants = (area.participants || []).some(
    (p) => p?.is_active !== false
  );
  // A deactivated person cannot be saved as an assignee anyway, so
  // proposing them would be a dead end rather than a convenience.
  return Boolean(activeAssignee || activeParticipants);
}

/**
 * @param {object|null} area          config for the newly chosen category
 * @param {boolean} peopleTouched     has the user decided anything?
 * @param {string|null} suggestedFrom category the current people came from
 * @returns {"apply"|"clear"|"none"}
 */
export function resolveCategoryChange({
  area,
  peopleTouched,
  suggestedFrom,
}) {
  // A human decision outranks every configuration. First, deliberately.
  if (peopleTouched) return "none";
  if (hasUsableDefaults(area)) return "apply";
  // Only ever withdraws OUR OWN proposal — reaching here means nothing
  // was manually set, so there is nothing of the user's to lose.
  if (suggestedFrom) return "clear";
  return "none";
}

/** The people an area proposes: the assignee, then the participants,
 * with inactive people dropped and the assignee never repeated. */
export function suggestionPeople(area) {
  const assignee = area?.default_assigned_to_user;
  const assigneeId =
    assignee && assignee.is_active !== false ? String(assignee.id) : "";
  const participantIds = (area?.participants || [])
    .filter((p) => p?.is_active !== false)
    .map((p) => String(p.id))
    .filter((id) => id !== assigneeId);
  return { assigneeId, participantIds };
}
